import type { ModuleMigrationDef } from "@kubdee/sdk";

// ============================================================================
// 002_migrate_to_connectors — P5b migration (issue #239).
//
// Chat module stops managing Facebook pages directly. Instead it references
// `connector_instance_id` from the connectors module and subscribes to the
// event bus for inbound messages.
//
// Safety: chat has NO live customers at the time of this migration, so a
// clean DROP + CREATE is acceptable. If this ever runs on a DB with data,
// the DROP will wipe it — verify `chat_conversations` is empty first.
// ============================================================================
export const migration: ModuleMigrationDef = {
  key: "002_migrate_to_connectors",
  version: "2.0.0",
  sql: `
DROP TABLE IF EXISTS \`chat_messages\`;
DROP TABLE IF EXISTS \`chat_conversations\`;
DROP TABLE IF EXISTS \`chat_facebook_pages\`;

CREATE TABLE \`chat_conversations\` (
\t\`id\` text PRIMARY KEY NOT NULL,
\t\`company_id\` text NOT NULL,
\t\`connector_instance_id\` text NOT NULL,
\t\`channel_type\` text DEFAULT 'facebook-messenger' NOT NULL,
\t\`external_user_id\` text NOT NULL,
\t\`sender_name\` text,
\t\`sender_avatar\` text,
\t\`status\` text DEFAULT 'bot' NOT NULL,
\t\`assigned_to\` text,
\t\`last_message_at\` text,
\t\`last_message_preview\` text,
\t\`unread_count\` integer DEFAULT 0 NOT NULL,
\t\`ai_agent_id\` text,
\t\`metadata\` text,
\t\`created_at\` text NOT NULL,
\t\`created_by\` text,
\t\`updated_at\` text NOT NULL,
\t\`updated_by\` text,
\t\`deleted_at\` text,
\t\`deleted_by\` text
);
CREATE INDEX \`chat_conversations_company_status_idx\` ON \`chat_conversations\` (\`company_id\`, \`status\`);
-- Partial unique index: at most ONE active conversation per
-- (connector_instance, external_user). Closed / soft-deleted rows do NOT
-- occupy the slot so the next message from the same user can start a fresh
-- conversation. Prevents the race H3 flagged in the #239 review where two
-- parallel inbound webhooks would otherwise create two rows.
CREATE UNIQUE INDEX \`chat_conversations_active_unique_idx\`
  ON \`chat_conversations\` (\`connector_instance_id\`, \`external_user_id\`)
  WHERE \`deleted_at\` IS NULL AND \`status\` != 'closed';

CREATE TABLE \`chat_messages\` (
\t\`id\` text PRIMARY KEY NOT NULL,
\t\`conversation_id\` text NOT NULL,
\t\`direction\` text NOT NULL,
\t\`sender_type\` text NOT NULL,
\t\`content\` text NOT NULL,
\t\`message_type\` text DEFAULT 'text' NOT NULL,
\t\`provider_message_id\` text,
\t\`metadata\` text,
\t\`created_at\` text NOT NULL,
\t\`created_by\` text,
\t\`deleted_at\` text,
\t\`deleted_by\` text,
\tFOREIGN KEY (\`conversation_id\`) REFERENCES \`chat_conversations\`(\`id\`) ON UPDATE no action ON DELETE no action
);
CREATE INDEX \`chat_messages_conversation_id_idx\` ON \`chat_messages\` (\`conversation_id\`);
-- Partial unique index on inbound provider messages: a provider retrying the
-- same event cannot land twice in the audit log (H6 from the #239 review).
-- Outbound messages share NULL provider_message_id at save time and fill it
-- in later; the partial index sidesteps them.
CREATE UNIQUE INDEX \`chat_messages_provider_unique_idx\`
  ON \`chat_messages\` (\`conversation_id\`, \`provider_message_id\`)
  WHERE \`provider_message_id\` IS NOT NULL;
  `.trim(),
};
